import { useState, useRef, useEffect } from 'react';
import { X, Trash2, Save, ImagePlus, Check } from 'lucide-react';
import { chPostService } from '../services/postService';
import { chProfileService } from '../services';
import { chNotificationService } from '../services/notificationService';
import { useChAuth } from '../contexts/ChAuthContext';
import { AiCaptionPanel } from './AiCaptionPanel';
import { MediaListEditor } from './MediaListEditor';
import { isVideoUrl } from './MediaPreview';
import { getAssigneeIds, getMediaUrls, getPlatforms, VIDEO_CONTENT_TYPES } from '../lib/posts';
import type { ChPost, ChPostPlatform, ChPostContentType, ChProfile } from '../types';
import { PLATFORM_LABELS, CONTENT_TYPE_LABELS, ROLE_LABELS, isChStaffRole } from '../types';

interface Props {
  projectId: string;
  post?: ChPost | null;
  /** Prefilled "what changed" when a new version answers the client. */
  initialVersionNote?: string;
  onClose: () => void;
  onSaved: (post: ChPost) => void;
}

function Toggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={active}
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
        active ? 'bg-[var(--ch-ink)] text-white' : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:ring-[var(--ch-ink)]'
      }`}
    >
      {active && <Check className="w-3 h-3" />}
      {children}
    </button>
  );
}

export function PostSlideOver({ projectId, post, initialVersionNote, onClose, onSaved }: Props) {
  const { user } = useChAuth();
  const coverRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadingCover, setUploadingCover] = useState(false);
  const isEdit = !!post?.id;

  const [form, setForm] = useState({
    title: post?.title || '',
    caption: post?.caption || '',
    platforms: post ? getPlatforms(post) : ['instagram'] as ChPostPlatform[],
    content_type: post?.content_type || 'reels' as ChPostContentType,
    scheduled_date: post?.scheduled_date?.split('T')[0] || '',
    due_date: post?.due_date?.split('T')[0] || '',
    external_link: post?.external_link || '',
    media_urls: post ? getMediaUrls(post) : [] as string[],
    thumbnail_url: post?.thumbnail_url || '',
    assignee_ids: post ? getAssigneeIds(post) : [] as string[],
  });
  const [versionNote, setVersionNote] = useState(initialVersionNote || '');
  const [team, setTeam] = useState<ChProfile[]>([]);

  useEffect(() => {
    chProfileService.getAll()
      .then(all => setTeam(all.filter(p => isChStaffRole(p.role))))
      .catch(() => setTeam([]));
  }, []);

  const isVideo = VIDEO_CONTENT_TYPES.includes(form.content_type) || form.media_urls.some(isVideoUrl);
  const showCover = isVideo || !!form.thumbnail_url;
  const dueAfterPublication = !!form.due_date && !!form.scheduled_date && form.due_date > form.scheduled_date;
  const contentChanged = isEdit && (
    form.title !== post!.title
    || (form.caption || null) !== (post!.caption || null)
    || (form.thumbnail_url || null) !== (post!.thumbnail_url || null)
    || JSON.stringify(form.media_urls) !== JSON.stringify(getMediaUrls(post!))
  );

  function toggle<K extends 'platforms' | 'assignee_ids'>(key: K, value: string) {
    setForm(f => {
      const list = f[key] as string[];
      const next = list.includes(value) ? list.filter(v => v !== value) : [...list, value];
      return { ...f, [key]: next };
    });
  }

  // Tell whoever was just made responsible (not yourself, not who already was)
  async function notifyNewAssignees(saved: ChPost) {
    if (!user) return;
    const before = new Set(post?.id ? getAssigneeIds(post) : []);
    const added = form.assignee_ids.filter(id => id !== user.id && !before.has(id));
    await Promise.all(added.map(id => chNotificationService.create({
      user_id: id,
      post_id: saved.id,
      project_id: saved.project_id,
      type: 'assigned',
      message: `${user.name} atribuiu-te "${saved.title}"`,
    }).catch(() => { /* the post is saved */ })));
  }

  async function handleCoverUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingCover(true);
    try {
      const url = await chPostService.uploadMedia(file, projectId);
      setForm(f => ({ ...f, thumbnail_url: url }));
    } catch {
      setError('Erro ao carregar a capa');
    } finally {
      setUploadingCover(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('O título é obrigatório');
      return;
    }
    if (form.platforms.length === 0) {
      setError('Escolhe pelo menos uma plataforma');
      return;
    }

    setError('');
    setSaving(true);

    const fields = {
      title: form.title,
      caption: form.caption || null,
      platform: form.platforms[0],
      platforms: form.platforms,
      content_type: form.content_type,
      scheduled_date: form.scheduled_date || null,
      due_date: form.due_date || null,
      external_link: form.external_link || null,
      media_url: form.media_urls[0] || null,
      media_urls: form.media_urls,
      thumbnail_url: form.thumbnail_url || null,
      assignee_id: form.assignee_ids[0] || null,
      assignee_ids: form.assignee_ids,
    };
    const versionInfo = { note: versionNote, authorId: user?.id, authorName: user?.name };

    try {
      if (isEdit) {
        const updated = await chPostService.update(post!.id, fields, versionInfo);
        await notifyNewAssignees(updated);
        onSaved(updated);
      } else {
        const created = await chPostService.create({
          ...fields,
          project_id: projectId,
          status: 'ideia',
          client_decision: null,
          talent_id: null,
          created_by: user?.id || null,
        }, versionInfo);
        await notifyNewAssignees(created);
        onSaved(created);
      }
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-md bg-[var(--ch-bg)] h-full overflow-y-auto ch-animate-in"
        style={{ animation: 'ch-slide-in 0.25s ease-out' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[var(--ch-bg)] border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <h2 className="ch-heading ch-heading-md">
            {isEdit ? 'Editar Post' : 'Novo Post'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-200 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {error && (
            <div className="p-3 rounded-xl bg-red-50 text-red-700 text-sm font-medium">
              {error}
            </div>
          )}

          <div>
            <label className="ch-label">Título *</label>
            <input
              type="text"
              className="ch-input"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Nome do post"
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="ch-label mb-0">Legenda / Copy</label>
              <AiCaptionPanel
                projectId={projectId}
                postId={post?.id}
                title={form.title}
                platform={form.platforms[0] || 'instagram'}
                contentType={form.content_type}
                scheduledDate={form.scheduled_date}
                currentCaption={form.caption}
                onSelect={(caption) => setForm(f => ({ ...f, caption }))}
              />
            </div>
            <textarea
              className="ch-textarea"
              value={form.caption}
              onChange={e => setForm(f => ({ ...f, caption: e.target.value }))}
              placeholder="Escreve a legenda aqui..."
              rows={4}
            />
          </div>

          <div>
            <label className="ch-label">Plataformas *</label>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Plataformas">
              {(Object.entries(PLATFORM_LABELS) as [ChPostPlatform, string][]).map(([val, label]) => (
                <Toggle key={val} active={form.platforms.includes(val)} onClick={() => toggle('platforms', val)}>
                  {label}
                </Toggle>
              ))}
            </div>
          </div>

          <div>
            <label className="ch-label">Tipo</label>
            <select
              className="ch-select"
              value={form.content_type}
              onChange={e => setForm(f => ({ ...f, content_type: e.target.value as ChPostContentType }))}
            >
              {Object.entries(CONTENT_TYPE_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="ch-label">Data de publicação</label>
                <input
                  type="date"
                  className="ch-input"
                  value={form.scheduled_date}
                  onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="ch-label">Prazo interno</label>
                <input
                  type="date"
                  className="ch-input"
                  value={form.due_date}
                  max={form.scheduled_date || undefined}
                  onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                />
              </div>
            </div>
            <p className={`text-[11px] mt-1.5 ${dueAfterPublication ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
              {dueAfterPublication
                ? 'O prazo interno é depois da data de publicação.'
                : 'Prazo interno: até quando tem de estar pronto para revisão. Se ficar vazio, o prazo é a data de publicação.'}
            </p>
          </div>

          <div>
            <label className="ch-label">Responsáveis</label>
            {team.length === 0 ? (
              <p className="text-xs text-gray-400">A carregar a equipa…</p>
            ) : (
              <div className="flex flex-wrap gap-2" role="group" aria-label="Responsáveis">
                {team.map(p => (
                  <Toggle key={p.id} active={form.assignee_ids.includes(p.id)} onClick={() => toggle('assignee_ids', p.id)}>
                    {p.name}
                    <span className={form.assignee_ids.includes(p.id) ? 'text-white/60' : 'text-gray-400'}>· {ROLE_LABELS[p.role]}</span>
                  </Toggle>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="ch-label">Link Externo</label>
            <input
              type="url"
              className="ch-input"
              value={form.external_link}
              onChange={e => setForm(f => ({ ...f, external_link: e.target.value }))}
              placeholder="https://..."
            />
          </div>

          <div>
            <label className="ch-label">Media</label>
            <MediaListEditor
              urls={form.media_urls}
              onChange={media_urls => setForm(f => ({ ...f, media_urls }))}
              upload={file => chPostService.uploadMedia(file, projectId)}
              onError={setError}
            />
          </div>

          {showCover && (
            <div>
              <label className="ch-label">Capa / miniatura</label>
              {form.thumbnail_url ? (
                <div className="flex items-start gap-3">
                  <div className="relative w-24 aspect-[9/16] rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                    <img src={form.thumbnail_url} alt="Capa" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      aria-label="Remover capa"
                      onClick={() => setForm(f => ({ ...f, thumbnail_url: '' }))}
                      className="absolute top-1 right-1 p-1 rounded bg-red-500 text-white"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  <button type="button" onClick={() => coverRef.current?.click()} className="ch-btn ch-btn-ghost ch-btn-sm">
                    <ImagePlus className="w-3.5 h-3.5" />
                    {uploadingCover ? 'A enviar...' : 'Trocar capa'}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => coverRef.current?.click()}
                  disabled={uploadingCover}
                  className="w-full h-20 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center gap-2 text-gray-400 hover:border-[var(--ch-ink)] hover:text-[var(--ch-ink)] transition-colors"
                >
                  <ImagePlus className="w-4 h-4" />
                  <span className="text-xs font-medium">{uploadingCover ? 'A enviar...' : 'Carregar capa desenhada à parte'}</span>
                </button>
              )}
              <p className="text-[11px] text-gray-400 mt-1.5">Aparece nos cartões e como capa do vídeo para o cliente.</p>
              <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
            </div>
          )}

          {contentChanged && (
            <div className="p-3 rounded-xl bg-white ring-1 ring-gray-200">
              <label className="ch-label">O que mudou nesta versão? (opcional)</label>
              <input
                type="text"
                className="ch-input"
                value={versionNote}
                onChange={e => setVersionNote(e.target.value)}
                placeholder="Ex.: nova capa, legenda revista"
              />
              <p className="text-[11px] text-gray-400 mt-1.5">
                Guarda como v{post!.current_version + 1}. A versão anterior e os seus ficheiros ficam no histórico.
              </p>
            </div>
          )}

          <div className="sticky bottom-0 pt-4 pb-2 bg-[var(--ch-bg)]">
            <button
              type="submit"
              disabled={saving}
              className="ch-btn ch-btn-primary w-full ch-btn-lg"
            >
              <Save className="w-4 h-4" />
              {saving ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
